// Post-launch Gender UX — friendly one-time profile-completion nudge.
//
// Renders on Home only when the viewer is an approved member whose
// profile.gender is not yet set. Provides a single-tap Male / Female
// setter that PATCHes /players/profile via the existing endpoint.
//
// Dismissal rules:
//   • "Not now" → dismissed for the current tab session only
//     (sessionStorage). Never nags across pages.
//   • Saving gender → refreshUser() drops user.gender != null, which
//     unmounts this component permanently.
//
// Registration remains the backend safety net for the rare case where
// a member never sees this nudge and later attempts a gender-restricted
// event.
import React, { useState } from 'react';
import { toast } from 'sonner';
import { X, User } from '@phosphor-icons/react';
import { playerAPI } from '../../lib/api';

const DISMISS_KEY = 'tl_gender_nudge_dismissed_v1';

export default function ProfileGenderNudge({ user, accountStatus, onSaved }) {
  const initiallyDismissed =
    typeof window !== 'undefined' &&
    window.sessionStorage.getItem(DISMISS_KEY) === '1';
  const [dismissed, setDismissed] = useState(initiallyDismissed);
  const [busy, setBusy] = useState(false);

  const shouldShow =
    !!user &&
    accountStatus === 'approved' &&
    !user.gender &&
    !dismissed;
  if (!shouldShow) return null;

  const dismiss = () => {
    try { window.sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  const save = async (g) => {
    setBusy(true);
    try {
      await playerAPI.updateProfile({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || null,
        usta_rating: user.usta_rating || null,
        gender: g,
        email_opt_in: !!user.email_opt_in,
        sms_opt_in: !!user.sms_opt_in,
      });
      toast.success('Thanks — profile updated.');
      await onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="profile-gender-nudge"
      role="status"
      className="bg-[#fffbf0] border border-[#eddb9f] rounded-2xl p-4 sm:p-5 relative"
    >
      <button
        type="button"
        data-testid="profile-gender-nudge-dismiss"
        onClick={dismiss}
        aria-label="Not now"
        className="absolute top-3 right-3 text-[#8a6a1c] hover:text-[#013523] p-1 rounded-lg"
      >
        <X size={16} weight="bold" />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-[#c9a93a]/15 flex items-center justify-center shrink-0">
          <User size={20} weight="duotone" className="text-[#8a6a1c]" />
        </div>
        <div className="flex-1 min-w-0 pr-6">
          <p className="text-sm text-[#013523] font-medium">One quick update</p>
          <p className="text-xs text-[#2a332c] mt-1 leading-relaxed">
            We're adding more Men's, Women's and Mixed events. Please update your
            profile so we can show the right events and partner choices.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="profile-gender-nudge-male"
              disabled={busy}
              onClick={() => save('male')}
              className="text-xs px-4 py-2 rounded-full bg-[#015e3f] text-white font-medium hover:bg-[#013523] transition-colors disabled:opacity-60 min-h-[36px]"
            >
              Male
            </button>
            <button
              type="button"
              data-testid="profile-gender-nudge-female"
              disabled={busy}
              onClick={() => save('female')}
              className="text-xs px-4 py-2 rounded-full bg-[#015e3f] text-white font-medium hover:bg-[#013523] transition-colors disabled:opacity-60 min-h-[36px]"
            >
              Female
            </button>
            <button
              type="button"
              data-testid="profile-gender-nudge-not-now"
              disabled={busy}
              onClick={dismiss}
              className="text-xs px-3 py-2 rounded-full text-[#6a766e] hover:text-[#013523] font-medium disabled:opacity-60 min-h-[36px]"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
